import { getAttachmentType } from '@helpers/build-attachment';
import { getFileSizeString } from '@helpers/document-info';
import useFlag from '@hooks/use-flag';
import useLastCallback from '@hooks/use-last-callback';
import type { Attachment } from '@interfaces/attachment';
import getFilesFromDataTransferItems from '@pages/ChatFeed/MiddleColumn/Composer/helpers/get-files-from-data-transfer-items';
import buildClassName from '@utils/build-class-name';
import { Alert } from 'antd';
import type { DragEvent, FC, MouseEvent as ReactMouseEvent } from 'react';
import { useEffect, useRef } from 'react';

import AttachmentItem from './AttachmentItem';
import styles from './AttachmentList.module.scss';

const ATTACHMENTS_TOTAL_SIZE_LIMIT = 100 * 1024 * 1024;
const ATTACHMENT_PHOTO_SIZE_LIMIT = 10 * 1024 * 1024;
const ATTACHMENT_FILE_SIZE_LIMIT = 50 * 1024 * 1024;

const DROP_LEAVE_TIMEOUT_MS = 150;

interface OwnProps {
  attachments: Attachment[];
  onError?: (message?: string) => void;
  onAttachmentsUpdate?: (attachments: Attachment[]) => void;
  onAttachmentsAppend?: (file: File[]) => void;
}

const AttachmentList: FC<OwnProps> = ({ attachments, onAttachmentsAppend, onAttachmentsUpdate, onError }) => {
  const hideTimeoutRef = useRef<number>();
  const [isHovered, markHovered, unmarkHovered] = useFlag();

  const handleDragLeave = useLastCallback((e: DragEvent<HTMLElement>) => {
    const { relatedTarget: toTarget, target: fromTarget } = e;

    // Esc button pressed during drag event
    if ((fromTarget as HTMLDivElement).matches(`.${styles.dropTarget}`) && !toTarget) {
      hideTimeoutRef.current = window.setTimeout(unmarkHovered, DROP_LEAVE_TIMEOUT_MS);
    }

    // Prevent DragLeave event from firing when the pointer moves inside the modal that contains drop target
    if (fromTarget && (fromTarget as HTMLElement).closest(`.${styles.hovered}`)) {
      return;
    }

    if (toTarget) {
      e.stopPropagation();
    }

    unmarkHovered();
  });

  const handleFilesDrop = useLastCallback(async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    unmarkHovered();

    const { dataTransfer } = e;

    const files = await getFilesFromDataTransferItems(dataTransfer.items);
    if (files?.length && onAttachmentsAppend) {
      onAttachmentsAppend(files);
    }
  });

  const handleDragOver = useLastCallback((e: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();

    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
  });

  const totalSizeOfAttachments = attachments.reduce((sum, attachment) => sum + attachment.size, 0);
  const individualFileExceedsSizeLimit = attachments.some((attachment) => {
    const type = getAttachmentType(attachment);
    return (
      (type === 'photo' && attachment.size >= ATTACHMENT_PHOTO_SIZE_LIMIT) ||
      attachment.size >= ATTACHMENT_FILE_SIZE_LIMIT
    );
  });
  const error =
    totalSizeOfAttachments >= ATTACHMENTS_TOTAL_SIZE_LIMIT
      ? `Attachments exceed the limit of ${getFileSizeString(ATTACHMENTS_TOTAL_SIZE_LIMIT)}`
      : individualFileExceedsSizeLimit
        ? `Attachment size limit is ${getFileSizeString(ATTACHMENT_PHOTO_SIZE_LIMIT)} for photos, and ${getFileSizeString(
            ATTACHMENT_FILE_SIZE_LIMIT,
          )} for other files.`
        : undefined;

  useEffect(() => {
    onError?.(error);
  }, [error, onError]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const handleDelete = useLastCallback((index: number) => {
    if (onAttachmentsUpdate) {
      onAttachmentsUpdate(attachments.filter((_, i) => i !== index));
    }
  });

  return (
    <div className={buildClassName(isHovered && styles.hovered)}>
      {error && <Alert style={{ margin: '1rem 0' }} message={error} type="error" />}
      {!error && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
          className={styles.dropTarget}
          onDragEnter={onAttachmentsAppend && markHovered}
          onDrop={onAttachmentsAppend && handleFilesDrop}
          onDragOver={onAttachmentsAppend && handleDragOver}
          onDragLeave={onAttachmentsAppend && handleDragLeave}
          onClick={onAttachmentsAppend && unmarkHovered}
          data-attach-description="Add Items"
        >
          <svg className={styles.dropOutlineContainer}>
            <rect className={styles.dropOutline} x="0" y="0" width="100%" height="100%" rx="8" />
          </svg>
          <div className={buildClassName(styles.attachments, 'custom-scroll')}>
            {attachments.map((attachment, i) => (
              <AttachmentItem
                attachment={attachment}
                index={i}
                key={attachment.uniqueId || i}
                onDelete={onAttachmentsUpdate && handleDelete}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentList;
